import { css } from '@emotion/react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import Checkbox from '../../OC/oc-checkbox';
import RichText from '../../OC/oc-rich-text';

const containerStyle = css`
  .inputs-container {
    margin-top: 1rem;
  }
  .checkbox-with-input {
    padding: 0 0 12px 0;
  }
  .field-title {
    display: inline-block;
    position: relative;
    width: 100%;
    font-size: 1em;
    font-weight: 700;
    margin-bottom: 0.5em;
  }
  .error {
    color: #940420;
    font-size: 12px;
    font-weight: bold;
  }
`;

const CheckboxWithInputField = props => {
  const { idSchema, schema, uiSchema = {}, formData = {}, onChange, required, disabled, readonly, errorSchema } = props;

  const uiOptions = uiSchema['ui:options'] || {};

  const enumValues = useMemo(() => {
    const values = schema?.properties?.selected?.items?.enum;
    return Array.isArray(values) ? values : [];
  }, [schema]);

  const labelsByValue = uiOptions.labels || {};
  const inputLabelsByValue = uiOptions.inputLabels || {};
  const showCustomTitle = uiOptions.showCustomTitle !== false;
  const title = schema?.title || '';

  const selected = Array.isArray(formData?.selected) ? formData.selected : [];

  const isChecked = value => selected.includes(value);

  const handleToggle = (optionValue, nextChecked) => {
    const nextSelected = nextChecked
      ? Array.from(new Set([...(selected || []), optionValue]))
      : (selected || []).filter(v => v !== optionValue);

    const nextData = { ...(formData || {}), selected: nextSelected };
    if (!nextChecked) {
      const key = `${optionValue}_input`;
      if (key in nextData) delete nextData[key];
    }
    onChange(nextData);
  };

  const handleInputChange = (optionValue, text) => {
    onChange({ ...(formData || {}), [`${optionValue}_input`]: text });
  };

  const checkRequired = selected.length === 0 ? required : false;

  return (
    <div id={idSchema?.$id} css={containerStyle} aria-disabled={disabled} aria-readonly={readonly}>
      {showCustomTitle && title && (
        <label className="field-title" htmlFor={idSchema?.$id}>
          {title}
          {required && '*'}
        </label>
      )}
      <div className="checkboxes">
        {enumValues.map((value, index) => (
          <div key={value} className="checkbox checkbox-with-input">
            <Checkbox
              id={`${idSchema?.$id}_option_${index}`}
              checked={isChecked(value)}
              required={checkRequired && index === 0}
              disabled={disabled}
              onChange={event => handleToggle(value, event.target.checked)}
            >
              <RichText content={labelsByValue[value] || value} />
            </Checkbox>
          </div>
        ))}
      </div>

      <div className="inputs-container">
        {enumValues.map(value => {
          const checked = isChecked(value);
          const inputKey = `${value}_input`;
          const inputValue = formData?.[inputKey] || '';
          const label = inputLabelsByValue[value] || schema?.properties?.[inputKey]?.title || '';
          return (
            <div key={value} className={`${checked ? 'visible' : 'hidden'}`}>
              <div className="form-group field field-string">
                <label htmlFor={`${idSchema?.$id}_${inputKey}`} className="css-fojbbv">
                  <span>
                    <span>
                      {label}
                      {checked ? '*' : ''}
                    </span>
                  </span>
                </label>
                <input
                  type="text"
                  required={checked}
                  id={`${idSchema?.$id}_${inputKey}`}
                  name={`${idSchema?.$id}_${inputKey}`}
                  className="form-control"
                  value={inputValue}
                  disabled={disabled || readonly}
                  onChange={event => handleInputChange(value, event.target.value)}
                />
                {/* eslint-disable-next-line no-underscore-dangle, max-len */}
                {errorSchema?.[inputKey] && <p className="error">{errorSchema?.[inputKey].__errors.join()}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

CheckboxWithInputField.defaultProps = {
  formData: {},
  required: false,
  errorSchema: {},
};

CheckboxWithInputField.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  schema: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
  uiSchema: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  formData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  disabled: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  readonly: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
  idSchema: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  errorSchema: PropTypes.object,
};

export default CheckboxWithInputField;
